import { Form, Formik } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, CardTitle, Loading, Select, TextArea } from 'lib/releox-react';
import OrderInfo from '../../../components/BuyerOrderInfo';
import CallLogList from '../../../components/CallLogList';
import DatePicker from '../../../components/DatePicker';
import routes from '../../../configs/routes';
import AfterCallAction, {
  AfterCallBody,
  AfterCallEventOption,
} from '../../../store/after-call/AfterCallsAction';
import AfterCallsSelector from '../../../store/after-call/AfterCallsSelector';
import OrdersAction from '../../../store/order/OrdersAction';
import OrderQuery from '../../../store/order/OrdersQuery';
import OrdersSelector from '../../../store/order/OrdersSelector';
import UsersSelector from '../../../store/user/UsersSelector';

const eventOptions = [
  { value: AfterCallEventOption.EMPTY_EVENT, label: 'Choose event' },
  { value: AfterCallEventOption.NEW_DUE_DATE, label: 'New due date' },
  { value: AfterCallEventOption.INVOICE_PAID, label: 'Invoice paid' },
  { value: AfterCallEventOption.HAS_NOT_ARRIVED, label: 'Has not arrived' },
  { value: AfterCallEventOption.WANTS_A_NEW_SECURITY_BAG, label: 'Wants a new security bag' },
  { value: AfterCallEventOption.DOES_NOT_ANSWER, label: 'Does not answer' },
];

export default () => {
  const { id: orderId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const order = useSelector(OrdersSelector.selectOrder);
  const user = useSelector(UsersSelector.selectUser);
  const isLoading = useSelector(OrdersSelector.selectIsModelLoading);
  const isSMSLoading = useSelector(AfterCallsSelector.selectIsLoading);

  useEffect(() => {
    dispatch(OrdersAction.fetch(orderId, OrderQuery.onlyInclude));
  }, [dispatch, orderId]);

  const onSubmit = useCallback(
    (body: AfterCallBody) => {
      dispatch(AfterCallAction.onSubmit(order, user, body));
    },
    [dispatch, order, user]
  );

  const sendSMS = useCallback(() => {
    dispatch(AfterCallAction.sendMHSMS());
  }, [dispatch]);

  if (isLoading) return <Loading centeredVertical />;

  return (
    <Formik initialValues={AfterCallAction.getInitialValues(order)} onSubmit={onSubmit}>
      <div className="row">
        <div className="col-lg-4">
          <Card>
            <CardTitle>After call</CardTitle>
            <OrderInfo order={order} />
            <hr />
            <Form>
              <DatePicker name="dueDate" label="Due date" inline={false} />
              <Select name="event" label="Choose one" options={eventOptions} />
              <TextArea name="logMessage" label="Log message" />
              <Link to={routes.BUYER.AFTER_CALLS}>Back</Link>
              <Button id="submit" type="submit" className="float-right">
                Save
              </Button>
              {order.country === 'finland' && order.language === 'finnish' ? (
                <>
                  <hr />
                  <Button
                    id="send-mh-return-code"
                    className="float-right"
                    type="button"
                    onClick={sendSMS}
                    disabled={isSMSLoading}
                    disabledText="Loading...."
                  >
                    Send Matkahuolto return code
                  </Button>
                </>
              ) : (
                ''
              )}
            </Form>
          </Card>
        </div>
        <div className="col-lg-4">
          <Card>
            <TextArea label="General message" name="generalMessage" />
            <TextArea label="Message to valuation" name="valuationMessageFromBuyer" />
            <TextArea label="Message to teller" name="tellerMessageFromBuyer" />
          </Card>
          <Card>
            <CardTitle>Pickup locations</CardTitle>
            <ul>
              {order.shipmentOffices.map((shipmentOffice) => (
                <li
                  key={shipmentOffice.name}
                >{`${shipmentOffice.name}, ${shipmentOffice.address}, ${shipmentOffice.zip} ${shipmentOffice.city}`}</li>
              ))}
            </ul>
          </Card>
        </div>
        <div className="col-lg-4">
          <Card>
            <CardTitle>After call Log</CardTitle>
            <CallLogList selector={OrdersSelector.selectLogs} />
          </Card>
        </div>
      </div>
    </Formik>
  );
};
